import React from 'react';
import { Redirect, useHistory, useLocation, Link } from 'react-router-dom';
import { ServicesContext } from '~/services';
import { Form, FormGroup, ValidationMessage } from '~/components/Form';
import { Card } from '~/components/Card';
import { Spinner } from 'react-bootstrap';
import { enumValue } from '~/util';

// Type assertion for TypeScript 5.x compatibility with react-router-dom v5
const RedirectComponent = Redirect as any;
const LinkComponent = Link as any;

interface LoginProps {
    auth: TODO_TYPEME; // will come from Auth conversion
}

export function Login({ auth }: LoginProps) {
    const servicesContext = React.useContext(ServicesContext);
    const history = useHistory();
    const location = useLocation<{ from: { pathname: string } }>();

    const [email, setEmail] = React.useState('');
    const [loginError, setLoginError] = React.useState<string>();
    const [password, setPassword] = React.useState('');
    const [loadingLogin, setLoadingLogin] = React.useState(false);
    const [loadingOHID, setLoadingOHID] = React.useState(false);
    const [linkOHIDLoginFlow, setLinkOHIDLoginFlow] = React.useState(false);

    const getGenericLoginError = (code: string) =>
        `Error code: ${code}. Please contact the support team at HelpDesk@wellmed.net or 1-877-435-7576`;

    React.useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const responseCode = queryParams.get('response-code');
        const connectionError = String(enumValue(['response', 'code', 'OIDC_CONNECTION_ERROR']));
        const missingCodeError = String(
            enumValue(['response', 'code', 'OIDC_AUTH_CODE_NOT_SENT_ERROR'])
        );
        const notLinkedError = String(
            enumValue(['response', 'code', 'OIDC_ACCOUNT_NOT_LINKED_ERROR'])
        );
        const expiredLinkError = String(enumValue(['response', 'code', 'WELCOME_TOKEN_EXPIRED']));
        const duplicateLinkError = String(
            enumValue(['response', 'code', 'OIDC_LINKED_TO_ANOTHER_USER_ERROR'])
        );
        if (responseCode === connectionError) {
            setLoginError(
                `Error code: ${connectionError}. Sorry, an error occurred while connecting to One Healthcare ID. Please try again. If this problem persists, please contact One Healthcare ID support.`
            );
        } else if (responseCode === missingCodeError) {
            setLoginError(
                `Error code: ${missingCodeError}. You’ve been redirected here from One Healthcare ID. To access Zephyr, ${
                    servicesContext.user.hasFeatureToggle('auth_one_healthcare_id_only')
                        ? ''
                        : 'sign in with your email and password or '
                }select Agree & Continue with One Healthcare ID.`
            );
        } else if (responseCode === expiredLinkError) {
            setLoginError(
                `Error code: ${expiredLinkError}. Activation link has expired. Please contact the support team at HelpDesk@wellmed.net or 1-877-435-7576.`
            );
        } else if (responseCode === duplicateLinkError) {
            setLoginError(
                `Error code: ${duplicateLinkError}. This One Healthcare ID is already linked to another Zephyr account. Please
                        contact support at portalsupport@wellmed.net for assistance.`
            );
        } else if (responseCode && responseCode !== notLinkedError) {
            setLoginError(getGenericLoginError(responseCode));
        }
        setLinkOHIDLoginFlow(responseCode === notLinkedError);
    }, [location.search, servicesContext.user]);

    // Reset loading animation when navigating back to the page
    React.useEffect(() => {
        const handlePopState = (event: PageTransitionEvent) => {
            if (event.persisted) {
                setLoadingOHID(false);
            }
        };
        window.addEventListener('pageshow', handlePopState);
        return () => {
            window.removeEventListener('pageshow', handlePopState);
        };
    }, []);

    async function handleSubmit() {
        try {
            setLoadingLogin(true);
            await servicesContext.user.login({ email, password });
            setLoadingLogin(false);
            const { from } = location.state || { from: { pathname: '/dashboard' } };
            history.push(from.pathname);
        } catch (error) {
            setLoadingLogin(false);
            if (error.message === 'mfa') {
                history.push('/mfa');
            } else if (error.message === 'mfa-enrollment') {
                history.push('/mfa-enrollment');
            } else if (error.message === 'expired') {
                history.push(`/reset/expired/${error.code}`);
            } else {
                let errorMsg = 'Invalid username and password combination.';
                // Show x-response-code if it exists (for easier support)
                const responseCode = error?.response?.headers?.['x-response-code'];
                const userAlreadyLinkedError = String(
                    enumValue(['response', 'code', 'OIDC_USER_ALREADY_LINKED'])
                );
                if (responseCode === userAlreadyLinkedError) {
                    errorMsg = `Error code: ${userAlreadyLinkedError}. This Zephyr user is already linked to another One Healthcare ID account. Please contact support at portalsupport@wellmed.net for assistance.`;
                } else if (responseCode) {
                    errorMsg = getGenericLoginError(responseCode);
                }

                setLoginError(errorMsg);
            }
        }
    }

    function ohidLogin() {
        setLoadingOHID(true);
        window.open('/oidc', '_self');
    }

    function cancelOhidLink() {
        history.push('/');
        servicesContext.user.clearOidcSession();
        setLoginError('');
    }

    function getRedirects() {
        if (window.app.reset_password && window.app.reset_password.ready) {
            servicesContext.user.setResetEndpoint(window.app.reset_password.endpoint);
            return <RedirectComponent to="/reset" />;
        }
        if (auth.authenticated) {
            return <RedirectComponent to="/dashboard" />;
        }
        return false;
    }

    return (
        getRedirects() || (
            <>
                <Card title={linkOHIDLoginFlow ? 'Link One Healthcare ID' : 'Sign In'}>
                    {linkOHIDLoginFlow && (
                        <div className="permissions-alert permissions-alert-card">
                            <div>
                                Please sign in to Zephyr to finish linking your account with OHID.
                            </div>
                            <div>Not sure if you have a Zephyr account? Contact support.</div>
                        </div>
                    )}
                    {(linkOHIDLoginFlow ||
                        !servicesContext.user.hasFeatureToggle('auth_one_healthcare_id_only')) && (
                        <Form onSubmit={handleSubmit} values={{ email, password, loginError }}>
                            <FormGroup htmlFor="email" className="mb-3 row">
                                <label htmlFor="email" className="col-3 col-form-label">
                                    Email
                                </label>
                                <div className="col-9">
                                    <input
                                        type="email"
                                        required
                                        className="form-control"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                                <ValidationMessage type="required" className="offset-3 col-9">
                                    Email is required.
                                </ValidationMessage>
                                <ValidationMessage type="email" className="offset-3 col-9">
                                    Please enter a valid email.
                                </ValidationMessage>
                            </FormGroup>
                            <FormGroup htmlFor="password" className="mb-3 row">
                                <label htmlFor="password" className="col-3 col-form-label">
                                    Password
                                </label>
                                <div className="col-9">
                                    <input
                                        type="password"
                                        required
                                        className="form-control"
                                        id="password"
                                        autoComplete="off"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </div>
                                <ValidationMessage type="required" className="offset-3 col-9">
                                    Password is required.
                                </ValidationMessage>
                                <ValidationMessage
                                    type="external"
                                    rule={!!loginError}
                                    className="offset-3 col-9"
                                    showBeforeSubmit
                                >
                                    {loginError}
                                </ValidationMessage>
                            </FormGroup>
                            <div className="row">
                                <div className="col text-end">
                                    {linkOHIDLoginFlow && (
                                        <button
                                            type="button"
                                            className="btn btn-secondary me-2"
                                            onClick={cancelOhidLink}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loadingLogin}
                                    >
                                        {loadingLogin && (
                                            <Spinner
                                                animation="border"
                                                variant="light"
                                                size="sm"
                                                role="status"
                                                className="me-1"
                                            />
                                        )}
                                        {linkOHIDLoginFlow ? 'Continue' : 'Agree and Sign In'}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}

                    {servicesContext.user.hasFeatureToggle('one_healthcare_id') &&
                        !servicesContext.user.hasFeatureToggle('auth_one_healthcare_id_only') &&
                        !linkOHIDLoginFlow && <div className="separator mt-2 mb-2">Or</div>}
                    {servicesContext.user.hasFeatureToggle('one_healthcare_id') &&
                        !linkOHIDLoginFlow && (
                            <div className="row">
                                <div className="col text-center">
                                    <button
                                        type="button"
                                        className="btn btn-info w-100"
                                        disabled={loadingOHID}
                                        onClick={ohidLogin}
                                    >
                                        {loadingOHID && (
                                            <Spinner
                                                animation="border"
                                                variant="light"
                                                size="sm"
                                                role="status"
                                                className="me-1"
                                            />
                                        )}
                                        Agree & Continue with One Healthcare ID
                                    </button>
                                </div>
                            </div>
                        )}
                    {!linkOHIDLoginFlow &&
                        servicesContext.user.hasFeatureToggle('auth_one_healthcare_id_only') &&
                        loginError && <div className="mt-3 text-danger">{loginError}</div>}
                </Card>
                {(linkOHIDLoginFlow ||
                    !servicesContext.user.hasFeatureToggle('auth_one_healthcare_id_only')) && (
                    <div className="row mt-3">
                        <div className="col text-end">
                            <LinkComponent to="/forgot">Forgot Password?</LinkComponent>
                        </div>
                    </div>
                )}
                {servicesContext.user.hasFeatureToggle('auth_one_healthcare_id_only') &&
                    servicesContext.user.hasFeatureToggle('ohid_login_resource') && (
                        <div className="row mt-3">
                            <div className="col text-end">
                                <a
                                    href="https://wellmedetd.com/Zephyr/documents/Zephyr%20Sign-In%20Instructions.pdf"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Need help signing in?
                                </a>
                            </div>
                        </div>
                    )}
            </>
        )
    );
}
